/**
 * platformerSessionBundle.ts
 * World-agnostic session-bundle dispatcher for the platformer screen.
 *
 * The `/game` route hosts the platformer worlds. Today that is W1, W3,
 * W4, and the W5 stages (main 5-1..5-4 plus the secret 5-0, all dispatched
 * via the `worldId === 5` branch). The W5 StarEater boss arena (5-B) plugs
 * in here as its session wiring lands. This file owns that dispatch in one
 * place so the screen layer stays small and the per-world
 * `levelNSession.ts` modules stay focused on their own world's data.
 *
 * Space-shmup (W2) levels are routed through `/space-game` and use
 * {@link spaceLevelSession} — they intentionally never reach this
 * dispatcher.
 *
 * @module platformerSessionBundle
 */

import type { PlayScene } from '../scenes/PlayScene';
import type { TileMapResult } from './TileMapLoader';
import {
  getWorld1Session,
  initWorld1Scene,
  resolveWorld1LevelId,
  getNextWorld1LevelId,
  persistWorld1Completion,
} from './level1Session';
import {
  getWorld3Session,
  initWorld3Scene,
  resolveWorld3LevelId,
  getNextWorld3LevelId,
  persistWorld3Completion,
} from './level3Session';
import {
  getWorld4Session,
  initWorld4Scene,
  resolveWorld4LevelId,
  getNextWorld4LevelId,
  persistWorld4Completion,
} from './level4Session';
import {
  getWorld5Session,
  initWorld5Scene,
  resolveWorld5LevelId,
  getNextWorld5LevelId,
  persistWorld5Completion,
} from './level5Session';

/** Options accepted by {@link PlatformerSessionBundle.initScene}. */
export interface InitSceneOptions {
  /**
   * Whether the finale gate cutscene can be skipped. Only honoured by
   * worlds that have a finale (currently only W1). Ignored for W3/W4/W5.
   */
  canSkip: boolean;
  /**
   * When true, the scene ignores the persisted `lastCheckpointId` so the
   * player respawns at the level start. Used by the Restart button.
   */
  skipCheckpointRestore: boolean;
}

/**
 * Uniform session bundle. The route layer holds a single value of this
 * shape regardless of which world is loaded and never branches on
 * worldId itself.
 */
export interface PlatformerSessionBundle {
  /** Common-shape session — `World1Session`, `World3Session`, `World4Session`, and `World5Session` all satisfy this. */
  readonly session: { readonly id: string; readonly tileMap: TileMapResult };
  /** Initialise the PlayScene from this bundle. */
  initScene(scene: PlayScene, options: InitSceneOptions): void;
  /** Next normal-progression level id, or `null` if there is none. */
  getNext(levelId: string): string | null;
  /** Persist completion stats for this level. Fire-and-forget. */
  persist(levelId: string): Promise<void>;
  /**
   * Whether {@link initScene} honours `canSkip`. True for worlds with a
   * finale cutscene that needs a separate skip path (W1); false for
   * worlds with no cutscene wiring (W3, W4, W5). The route layer uses this
   * to avoid a redundant second `initScene` after the async save
   * reconcile lands — see web/screens/game.ts.
   */
  readonly honoursCanSkip: boolean;
}

/**
 * Build a session bundle from a raw `?level=` param value. Defaults to
 * W1 for any unknown or unparseable prefix so the screen never boots
 * into an empty bundle.
 *
 * @param rawLevel - Raw param value from the router (e.g. `'3-1'`).
 * @returns A {@link PlatformerSessionBundle} ready for the game-screen
 *   route to drive. Always non-null; on bad input the bundle resolves
 *   to W1 1-1 rather than throwing.
 */
export function buildPlatformerSessionBundle(
  rawLevel: string | string[] | undefined,
): PlatformerSessionBundle {
  const candidate = Array.isArray(rawLevel) ? rawLevel[0] : rawLevel;
  const prefix = (candidate ?? '').split('-')[0];
  const worldId = Number(prefix);

  if (worldId === 3) {
    const levelId = resolveWorld3LevelId(rawLevel);
    const session = getWorld3Session(levelId);
    return {
      session,
      initScene: (scene, opts) => initWorld3Scene(scene, session, opts.skipCheckpointRestore),
      getNext: getNextWorld3LevelId,
      persist: persistWorld3Completion,
      honoursCanSkip: false,
    };
  }

  if (worldId === 4) {
    const levelId = resolveWorld4LevelId(rawLevel);
    const session = getWorld4Session(levelId);
    return {
      session,
      initScene: (scene, opts) => initWorld4Scene(scene, session, opts.skipCheckpointRestore),
      getNext: getNextWorld4LevelId,
      persist: persistWorld4Completion,
      honoursCanSkip: false,
    };
  }

  if (worldId === 5) {
    const levelId = resolveWorld5LevelId(rawLevel);
    const session = getWorld5Session(levelId);
    return {
      session,
      initScene: (scene, opts) => initWorld5Scene(scene, session, opts.skipCheckpointRestore),
      getNext: getNextWorld5LevelId,
      persist: persistWorld5Completion,
      honoursCanSkip: false,
    };
  }

  // DECISION: Default to World 1 for unknown prefixes. Keeps the router
  // resilient to typos and means a stale bookmark to `/game` still
  // produces a playable scene instead of a blank canvas.
  const levelId = resolveWorld1LevelId(rawLevel);
  const session = getWorld1Session(levelId);
  return {
    session,
    initScene: (scene, opts) =>
      initWorld1Scene(scene, session, opts.canSkip, opts.skipCheckpointRestore),
    getNext: getNextWorld1LevelId,
    persist: persistWorld1Completion,
    honoursCanSkip: true,
  };
}
